import type { ReportPeriod } from "@/components/app/documents/report-period";
import { optionalDay, optionalInt, optionalOneOf } from "@/lib/search";

type ReportView = ReportPeriod["view"];

const VIEWS = [
  "current",
  "today",
  "month",
  "quarter",
  "year",
  "ytd",
  "custom",
  "yearOverview",
] as const satisfies readonly ReportView[];

/** A report period as it rides in the address. Only what differs from the default is written. */
export type PeriodSearch = {
  view?: ReportView;
  year?: number;
  month?: number;
  quarter?: number;
  from?: string;
  to?: string;
};

export function periodSearch(search: Record<string, unknown>): PeriodSearch {
  return {
    view: optionalOneOf(VIEWS, search.view),
    year: optionalInt(search.year, 1990, 2200),
    month: optionalInt(search.month, 1, 12),
    quarter: optionalInt(search.quarter, 1, 4),
    from: optionalDay(search.from),
    to: optionalDay(search.to),
  };
}

export function periodFromSearch(params: PeriodSearch, fallback: ReportPeriod): ReportPeriod {
  return {
    view: params.view ?? fallback.view,
    year: params.year ?? fallback.year,
    month: params.month ?? fallback.month,
    quarter: params.quarter ?? fallback.quarter,
    startDate: params.from ?? fallback.startDate,
    endDate: params.to ?? fallback.endDate,
  };
}

const same = <T>(a: T, b: T) => (a === b ? undefined : a);

export function periodToSearch(period: ReportPeriod, fallback: ReportPeriod): PeriodSearch {
  return {
    view: same(period.view, fallback.view),
    year: same(period.year, fallback.year),
    month: same(period.month, fallback.month),
    quarter: same(period.quarter, fallback.quarter),
    from: period.startDate === fallback.startDate ? undefined : period.startDate || undefined,
    to: period.endDate === fallback.endDate ? undefined : period.endDate || undefined,
  };
}
